import logger from './logger';

/**
 * グラフエクスポートユーティリティ
 * グラフ要素からSVGコンテンツを抽出して、エクスポート用に準備する
 */

/**
 * DOM要素からSVG要素を検索して取得
 */
export function findSvgElement(element: HTMLElement): SVGSVGElement | null {
  // 直接SVG要素の場合
  if (element instanceof SVGSVGElement) {
    return element;
  }

  // 子要素からSVGを検索
  const svg = element.querySelector('svg');
  if (svg) {
    return svg;
  }

  logger.warn('[graphExportUtils] No SVG element found in the provided element');
  return null;
}

/**
 * SVG要素をエクスポート用の文字列に変換
 * スタイルとネームスペースを適切に設定
 */
export function prepareSvgForExport(
  svg: SVGSVGElement, 
  title?: string,
  options?: { width?: number; height?: number }
): string {
  // SVGをクローン（元のDOMに影響を与えないため）
  const clonedSvg = svg.cloneNode(true) as SVGSVGElement;
  
  // サイズ設定の処理（width/heightは必須）
  if (options?.width && options?.height) {
    // 指定されたwidth/heightを設定
    clonedSvg.setAttribute('width', String(options.width));
    clonedSvg.setAttribute('height', String(options.height));
    
    // viewBoxも設定（アスペクト比を保持）
    if (!clonedSvg.getAttribute('viewBox')) {
      // 元のサイズからviewBoxを計算
      const rect = svg.getBoundingClientRect();
      if (rect.width && rect.height) {
        clonedSvg.setAttribute('viewBox', `0 0 ${rect.width} ${rect.height}`);
      } else {
        // フォールバック: 指定されたサイズをviewBoxにも使用
        clonedSvg.setAttribute('viewBox', `0 0 ${options.width} ${options.height}`);
      }
    }
  } else {
    // デフォルト: 現在のサイズをそのまま使用
    const rect = svg.getBoundingClientRect();
    if (rect.width && rect.height) {
      clonedSvg.setAttribute('width', String(rect.width));
      clonedSvg.setAttribute('height', String(rect.height));
      if (!clonedSvg.getAttribute('viewBox')) {
        clonedSvg.setAttribute('viewBox', `0 0 ${rect.width} ${rect.height}`);
      }
    } else {
      // フォールバック: デフォルトサイズ
      clonedSvg.setAttribute('width', '800');
      clonedSvg.setAttribute('height', '600');
      clonedSvg.setAttribute('viewBox', '0 0 800 600');
    }
  }
  
  // 必要な属性を設定
  if (!clonedSvg.getAttribute('xmlns')) {
    clonedSvg.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
  }
  if (!clonedSvg.getAttribute('xmlns:xlink')) {
    clonedSvg.setAttribute('xmlns:xlink', 'http://www.w3.org/1999/xlink');
  }

  // タイトルを追加（アクセシビリティのため）
  if (title) {
    const titleElement = document.createElementNS('http://www.w3.org/2000/svg', 'title');
    titleElement.textContent = title;
    
    // 既存のタイトルがある場合は置き換え
    const existingTitle = clonedSvg.querySelector('title');
    if (existingTitle) {
      existingTitle.replaceWith(titleElement);
    } else {
      clonedSvg.prepend(titleElement);
    }
  }

  // スタイルを埋め込む（外部CSSの参照を解決）
  embedStyles(clonedSvg);

  // SVG文字列を生成
  const serializer = new XMLSerializer();
  let svgString = serializer.serializeToString(clonedSvg);

  // XMLヘッダーを追加
  if (!svgString.startsWith('<?xml')) {
    svgString = '<?xml version="1.0" encoding="UTF-8"?>\n' + svgString;
  }

  return svgString;
}

/**
 * 計算されたスタイルをSVG要素に埋め込む
 * エクスポートされたSVGが独立して表示できるようにする
 */
function embedStyles(svg: SVGSVGElement): void {
  // すべてのSVG要素を取得
  const elements = svg.querySelectorAll('*');
  
  elements.forEach(element => {
    if (!(element instanceof SVGElement)) return;
    
    const computedStyle = window.getComputedStyle(element);
    const importantStyles = [
      'fill',
      'stroke',
      'stroke-width',
      'stroke-dasharray',
      'stroke-linecap',
      'stroke-linejoin',
      'opacity',
      'font-family',
      'font-size',
      'font-weight',
      'text-anchor',
      'dominant-baseline'
    ];

    // 重要なスタイルをインラインスタイルとして設定
    importantStyles.forEach(prop => {
      const value = computedStyle.getPropertyValue(prop);
      if (value && value !== 'none' && value !== 'auto' && value !== 'normal') {
        (element as unknown as HTMLElement).style.setProperty(prop, value);
      }
    });
  });

  // SVG自体の背景色を設定（もし背景がある場合）
  const svgComputedStyle = window.getComputedStyle(svg);
  const bgColor = svgComputedStyle.backgroundColor;
  if (bgColor && bgColor !== 'rgba(0, 0, 0, 0)' && bgColor !== 'transparent') {
    // 背景用のrect要素を追加
    const rect = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
    rect.setAttribute('width', svg.getAttribute('width') || '100%');
    rect.setAttribute('height', svg.getAttribute('height') || '100%');
    rect.setAttribute('fill', bgColor);
    svg.prepend(rect);
  }
}

/**
 * グラフ要素からSVGコンテンツを抽出
 * @param elementId - レポート要素のID
 * @param title - グラフのタイトル（オプション）
 * @param options - サイズ設定オプション
 * @returns SVG文字列またはnull
 */
export function extractSvgFromGraphElement(
  elementId: string, 
  title?: string,
  options?: { width?: number; height?: number; responsive?: boolean }
): string | null {
  const reportElement = document.querySelector(`[data-element-id="${elementId}"]`);
  
  if (!reportElement) {
    // data-element-idがない場合、IDで直接検索を試みる
    const elementById = document.getElementById(elementId);
    if (!elementById) {
      logger.error('[graphExportUtils] Report element not found', { elementId });
      return null;
    }
    
    const svg = findSvgElement(elementById as HTMLElement);
    if (!svg) {
      return null;
    }
    
    return prepareSvgForExport(svg, title, options);
  }

  const svg = findSvgElement(reportElement as HTMLElement);
  if (!svg) {
    return null;
  }

  return prepareSvgForExport(svg, title, options);
}

/**
 * SVGコンテンツのサイズ情報を取得
 */
export function getSvgDimensions(svg: SVGSVGElement): { width: number; height: number } | null {
  const width = svg.getAttribute('width');
  const height = svg.getAttribute('height');
  
  if (width && height) {
    return {
      width: parseFloat(width),
      height: parseFloat(height)
    };
  }

  // viewBoxから取得を試みる
  const viewBox = svg.getAttribute('viewBox');
  if (viewBox) {
    const [, , w, h] = viewBox.split(' ').map(parseFloat);
    if (w && h) {
      return { width: w, height: h };
    }
  }

  // getBoundingClientRectから取得
  const rect = svg.getBoundingClientRect();
  if (rect.width && rect.height) {
    return {
      width: rect.width,
      height: rect.height
    };
  }

  return null;
}